import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, ArrowRight, ChevronRight, Loader2, Sparkles, UserPlus, Gem, ListChecks,
  Target, ShieldCheck, LayoutDashboard, LifeBuoy,
} from "lucide-react";

import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { useCrmActions } from "@/context/CrmActionsContext";
import { onboardingService } from "@/services/onboardingService";
import { emitCrmChanged } from "@/services/crmService";

const STEP_ICON = {
  business_profile: ShieldCheck, brand_profile: Gem, first_contact: UserPlus,
  first_task_followup: ListChecks, first_goal: Target, license: ShieldCheck,
  review_dashboard: LayoutDashboard,
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actions = useCrmActions();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await onboardingService.get()); } catch { toast.error("Could not load setup"); }
  }, []);

  useEffect(() => {
    (async () => {
      try { setData(await onboardingService.start()); } catch { load(); }
    })();
  }, [load]);

  // Re-derive step completion when the user creates a record via a dialog.
  useEffect(() => {
    const h = () => load();
    window.addEventListener("crm:changed", h);
    return () => window.removeEventListener("crm:changed", h);
  }, [load]);

  if (!data) return <AppShell><div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div></AppShell>;

  const inAppAction = {
    first_contact: actions.openAddContact,
    first_task_followup: actions.openNewTask,
    first_goal: actions.openNewGoal,
    license: actions.openAddLicense,
  };

  const doStepCta = (step) => {
    const fn = inAppAction[step.key];
    if (fn) { fn(); return; }
    navigate(step.route);
  };

  const skip = async (key) => { setBusy(true); try { setData(await onboardingService.skipStep(key)); } catch (e) { toast.error(e?.message); } finally { setBusy(false); } };

  const finish = async () => {
    setBusy(true);
    try { await onboardingService.completeStep("review_dashboard"); emitCrmChanged(); toast.success("You're all set — welcome to AgentHQ"); navigate("/"); }
    catch (e) { toast.error(e?.message); setBusy(false); }
  };

  const skipAll = async () => { setBusy(true); try { await onboardingService.dismiss(); navigate("/"); } catch (e) { toast.error(e?.message); setBusy(false); } };

  const firstName = user?.first_name || "there";
  const nextStep = data.steps.find((s) => s.state === "todo");

  return (
    <AppShell>
      <div className="mx-auto max-w-3xl space-y-8" data-testid="onboarding-page">
        {/* Hero */}
        <div className="rounded-2xl border border-brand-navy/15 bg-gradient-to-br from-brand-navy to-brand-deep-blue p-6 text-white sm:p-8">
          <div className="flex items-center gap-2 text-brand-gold"><Sparkles className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">Welcome to AgentHQ</span></div>
          <h1 className="mt-3 font-brand text-2xl font-bold sm:text-3xl">Let's set up your workspace, {firstName}.</h1>
          <p className="mt-2 max-w-xl text-sm text-white/70">A few quick steps and AgentHQ will start organizing your follow-ups, tasks, goals and professional deadlines — all in one place.</p>
          <div className="mt-5">
            <div className="mb-1.5 flex items-center justify-between text-sm">
              <span className="font-semibold text-white">Your AgentHQ setup</span>
              <span className="text-white/70" data-testid="onboarding-progress-count">{data.completed_count} of {data.total_steps} complete</span>
            </div>
            <Progress value={data.percent} className="h-2 bg-white/15" data-testid="onboarding-progress-bar" />
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-3" data-testid="onboarding-steps">
          {data.steps.map((step, i) => {
            const Icon = STEP_ICON[step.key] || Check;
            const done = step.state === "complete";
            const skipped = step.state === "skipped";
            const isNext = nextStep && nextStep.key === step.key;
            return (
              <div key={step.key} data-testid={`onboarding-step-${step.key}`}
                className={`flex items-start gap-4 rounded-xl border p-4 transition-colors ${done ? "border-emerald-200 bg-emerald-50/40" : isNext ? "border-brand-gold/50 bg-brand-gold/5" : "border-border bg-white"}`}>
                <div className={`mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full ${done ? "bg-emerald-500 text-white" : "bg-brand-navy/10 text-brand-navy"}`}>
                  {done ? <Check className="h-5 w-5" /> : <Icon className="h-4.5 w-4.5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-brand-navy">{i + 1}. {step.title}</p>
                    {done && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700" data-testid={`onboarding-badge-${step.key}`}>Complete</span>}
                    {skipped && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Skipped</span>}
                  </div>
                  <p className="mt-0.5 text-sm text-muted-foreground">{step.description}</p>
                  {!done && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {step.key === "review_dashboard" ? (
                        <Button onClick={finish} disabled={busy} className="bg-brand-navy hover:bg-brand-deep-blue" data-testid="onboarding-finish">
                          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Finish & go to Dashboard <ArrowRight className="h-4 w-4" /></>}
                        </Button>
                      ) : (
                        <Button onClick={() => doStepCta(step)} variant={isNext ? "default" : "outline"} className={isNext ? "bg-brand-navy hover:bg-brand-deep-blue" : ""} data-testid={`onboarding-cta-${step.key}`}>
                          {step.cta_label} <ChevronRight className="h-4 w-4" />
                        </Button>
                      )}
                      {step.skippable && !skipped && (
                        <button onClick={() => skip(step.key)} disabled={busy} className="text-xs font-medium text-muted-foreground hover:text-foreground" data-testid={`onboarding-skip-${step.key}`}>Skip for now</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
          <button onClick={() => navigate("/help")} className="inline-flex items-center gap-1.5 text-sm text-accent hover:underline" data-testid="onboarding-help"><LifeBuoy className="h-4 w-4" /> Need help?</button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={skipAll} disabled={busy} data-testid="onboarding-skip-all">Skip setup for now</Button>
            <Button variant="outline" onClick={() => navigate("/")} data-testid="onboarding-go-dashboard">Go to Dashboard</Button>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
